import { useState, useContext, useEffect, useRef } from 'react'
import { AxiosResponse } from 'axios'
import { CommonCtx } from '../../context/CommonCtxProvider'
import { DELETE_WARNING, TIME_OFF_REQUEST, EMPLOYEE_TIME_OFF, HELP_TIME_OFF_PAGE, HELP_TIME_OFF_HISTORY, REQUEST_TIME_OFF, MY_TIME_OFF } from '../../constants/strings'
import { FaSearch } from 'react-icons/fa'
import { getAllUserTimeOffRequests, getAllByUser } from '../../helpers/timeOffHelper'
import { getStaffId, checkPermission, defaultPaginationEvent, defaultPagination } from '../../helpers/utils'
import { routes } from '../../constants/routes'
import { toastMessageInitialData, PageMode, PermissionKeys, SubPermissionKeys, MOBILE_VIEW_BREAKPOINT, DATE_FORMAT } from '../../constants/constants'
import { ToastMessageProps, TimeOffEntry, DatatablePagEvent, PaginationApi } from '../../constants/staticTypes'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { USERS_TIME_OFF_LIST } from '../../constants/queryKeys'
import Loader from '../../components/loader/Loader'
import TimeOffImage from '../../assets/images/time_off.svg'
import TimeOffRequestSideBar from '../../components/time_off_request/TimeOffRequestSideBar'
import ToastAlert from '../../components/alert/ToastAlert'
import useQueryHook from '../../hooks/useQueryHook'
import UserTimeOffRequestsDataGrid from '../../components/time_off_request/UserTimeOffRequestsDataGrid'
import WarningModal from '../../components/warning_modal/WarningModal'
import withSidebar from '../../hoc/withSidebar'
import { DebounceInput } from 'react-debounce-input'
import { JTranslation, TranslationContext, jTranslationText } from '../../helpers/jTranslate'
import { useWindowSize } from 'react-use'
import MlxPopover from '../../components/common/MlxPopover'
import FilterWrapper from '../../components/filter_wrapper/FilterWrapper'
import dayjs from 'dayjs'
import { DatePicker } from 'antd'
import { Dropdown } from 'primereact/dropdown'
import { capitalize } from 'lodash'

const statusList = [
	{ label: 'Pending', value: 'PENDING' },
	{ label: 'Approved', value: 'APPROVED' },
	{ label: 'Rejected', value: 'REJECTED' }
]

function TimeOffRequest() {
	const queryClient = useQueryClient()
	const location = useLocation()
	const currentPath = location.pathname
	const staffId = getStaffId()
	const { width } = useWindowSize()
	const breakPoint = MOBILE_VIEW_BREAKPOINT
	const prevCurrentPathRef = useRef(currentPath);

	// CONTEXT VARIABLE
	const { showSideBar, setShowSideBar } = useContext(CommonCtx)	
	const translationContext = useContext(TranslationContext)
	const { targetLanguage } = translationContext

	// STATE VARIABLE
	const [mode, setMode] = useState<PageMode>(PageMode.ADD)
	const [search, setSearch] = useState("")
	const [showWarning, setWarning] = useState(false)
	const [timeOffRequestList, setTimeOffRequestList] = useState<TimeOffEntry[]>([])
	const [toastMessage, setToastMessage] = useState<ToastMessageProps>(toastMessageInitialData)	
	const [translationText, setTranslatedText] = useState<{
		search: string,
	  }>({ 
		search: 'Search',
	});
	const [showTitle, setShowTitle] = useState(true)
	const [selectedWeek, setSelectedWeek] = useState<dayjs.Dayjs | null>(null)
	const [selectedStatus, setSelectedStatus] = useState<string | null>(statusList[0].value)
	const [dataTableEvent, setDataTableEvent] = useState<DatatablePagEvent>({...defaultPaginationEvent, sortField: 'startDate', sortOrder: -1});
	const [paginationData, setPaginationData] = useState<PaginationApi>(defaultPagination);
	const [urlParams, setUrlParams] = useState<string>(`?page=${(dataTableEvent.page ?? 0) + 1}&limit=${dataTableEvent.rows}&sort[startDate]=DESC&filters[actionStatus]=${statusList[0].value}`);

	// users list fetch success
	const onUserListFetchSuccess = (res: AxiosResponse<any, any>) => {
		const timeOffRequestListData = res.data.data.lists || [];
		setTimeOffRequestList(timeOffRequestListData as TimeOffEntry[])

		const paginationData = res?.data?.data?.pagination as PaginationApi;
		setPaginationData(paginationData);
	}

	// fetch users list
	const timeOffData = useQueryHook(
		USERS_TIME_OFF_LIST,
		() => {
			if (currentPath === routes.time_off && staffId) {
				return getAllByUser(staffId, urlParams);
			} else {
				return getAllUserTimeOffRequests(urlParams);
			}
		},
		onUserListFetchSuccess,
		undefined,
		false,
		false,
	);

	const filterTemplate = (
		<FilterWrapper 
			isFiltered={!!(selectedStatus || selectedWeek)} 
			clearFilter={() => {
				setSelectedStatus(null)
				setSelectedWeek(null)
			}}
		>
			<DatePicker
				placeholder='Select Week'
				className="mb-2 availability-date-picker w-100"
				onChange={(date, dateString) => {
					if (!date) {
						setSelectedWeek(null)
						return
					}

					const selectedDate = date ? dayjs(date) : dayjs()
					setSelectedWeek(selectedDate)
				}}
				picker="week"
				value={selectedWeek}
				format={(value) =>
					`${dayjs(value).startOf('week').format(DATE_FORMAT)} - ${dayjs(value)
						.endOf('week')
						.format(DATE_FORMAT)}`
				}
				showWeek={false}
				allowClear={true}
				style={{ borderRadius: '3px' }}
			/>
			<Dropdown 
				className='w-100'
				value={selectedStatus}
				options={statusList}
				itemTemplate={(option) => <span key={option.value}>{option.label}</span>}
				onChange={(e) => setSelectedStatus(e.target.value)}
				showClear={true}
				placeholder={'Status'}
				optionLabel='label'
				optionValue='value'
				style={{ borderRadius: '3px' }}
				pt={{ input: { style: { padding: '0.444rem 0.75rem' } } }}
			/>
		</FilterWrapper>
	)

	// get availability list with params
	const updateUrlParams = () => {
		let urlParams = `?page=${(dataTableEvent.page ?? 0) + 1}&limit=${dataTableEvent.rows}`;
		if (search) {
			urlParams += `&search=${search}`
		}
		if (selectedStatus) {
			urlParams += `&filters[actionStatus]=${selectedStatus}`
		}
		if (selectedWeek?.isValid()) {
			urlParams += `&filters[startDate_gte]=${selectedWeek.startOf('week').format('YYYY-MM-DD')}`;
			urlParams += `&filters[endDate_lte]=${selectedWeek.endOf('week').format('YYYY-MM-DD')}`;
		}
		if(dataTableEvent.sortField) {
			urlParams += `&sort[${dataTableEvent.sortField}]=${dataTableEvent.sortOrder === 1 ? 'ASC' : 'DESC'}`
		}
		return urlParams;
	}

	// Translate on load and language switch
	useEffect(() => {
		const fetchTranslation = async () => {
			try {
				const translations = await Promise.all([
					jTranslationText({ text: 'Search', typeCase: 'pascal', translationContext }),
				])
				
				setTranslatedText({
					search: translations[0] ?? 'Search',
				})
			} catch {
				setTranslatedText({
					search: 'Search',
				})
			}
		}
		fetchTranslation()
	}, [targetLanguage])

	useEffect(() => {
		// Update URL Params
		const newUrlParams = updateUrlParams();
		
		// If the params actually changed, refetch data
		if (newUrlParams !== urlParams || dataTableEvent?.forceUpdate) {
			setUrlParams(newUrlParams);
		}
	}, [search, selectedWeek, selectedStatus, dataTableEvent?.forceUpdate]);

	useEffect(() => {
		timeOffData.refetch();
	},[urlParams])

	useEffect(() => {
		if (prevCurrentPathRef.current && prevCurrentPathRef.current !== currentPath) {
			queryClient.removeQueries(USERS_TIME_OFF_LIST);
			setSearch("");
			setSelectedWeek(null);
			setSelectedStatus(statusList[0].value);
			setDataTableEvent(dataTableEvent => ({
				...defaultPaginationEvent, 
				sortField: 'startDate', 
				sortOrder: -1,
				forceUpdate: dataTableEvent.forceUpdate + 1
			}));
			setUrlParams(`?page=${(dataTableEvent.page ?? 0) + 1}&limit=${dataTableEvent.rows}&sort[startDate]=DESC&filters[actionStatus]=${statusList[0].value}`);
		}
		prevCurrentPathRef.current = currentPath;
	}, [currentPath]) // eslint-disable-line	

	if(timeOffData.isLoading) {
		return <Loader />
	}

	return (
		<>
			{/* Delete modal */}
			<WarningModal
				show={showWarning}
				title={DELETE_WARNING}
				onHide={() => {
					setWarning(false)
				}}
				callback={() => {
					setWarning(false)
				}}
			/>

			{/* toaster component */}
			<ToastAlert
				show={toastMessage.show}
				onClose={() => setToastMessage(toastMessageInitialData)}
				message={toastMessage.message}
				variant={toastMessage.variant}
			/>

			{/* side bar component  */}
			<TimeOffRequestSideBar pageMode={mode} setShowSideBar={setShowSideBar} setToastMessage={setToastMessage} />

			<div className="page-title mb-3 d-flex align-items-center">
				<h5 className="d-flex mb-0 align-items-center">
					<JTranslation typeCase="pascal" text={EMPLOYEE_TIME_OFF} />
					<MlxPopover data={{title:EMPLOYEE_TIME_OFF, body:currentPath === routes.time_off_requests ? HELP_TIME_OFF_PAGE : HELP_TIME_OFF_HISTORY}}/>
				</h5>
			</div>

			<div className="col-md-12 col-lg-12 mb-3 h-100 ">
				<div className="card h-100 ">
					<div className="card-header flex-grow-0">
						<div className="d-flex  align-items-center">
							<div className="flex-grow-1">
								{showTitle && 
									<h4 className="mb-0 title">{
										currentPath === routes.time_off_requests
											? <JTranslation typeCase="pascal" text={TIME_OFF_REQUEST+'s' } />
											: <JTranslation typeCase="pascal" text={MY_TIME_OFF} />
									}</h4>
								}
							</div>
							<div className="flex-grow-0 me-2 ms-1">
								<button
									className={"btn btn-custom-primary d-flex icon-only-btn " +
										(
											(currentPath === routes.time_off_requests 
												? !checkPermission(queryClient, PermissionKeys.MANAGE_TIME_OFF, SubPermissionKeys.ADD)
												: !checkPermission(queryClient, PermissionKeys.MANAGE_MY_TIME_OFF, SubPermissionKeys.MY_TIME_OFF)) &&
											' disabled'
										)
									}
									type="button"
									onClick={() => {
										if (
											currentPath === routes.time_off_requests 
												? checkPermission(queryClient, PermissionKeys.MANAGE_TIME_OFF, SubPermissionKeys.ADD)
												: checkPermission(queryClient, PermissionKeys.MANAGE_MY_TIME_OFF, SubPermissionKeys.MY_TIME_OFF)
										) {
											setShowSideBar(true)
										}
									}}
								>
									<i className="ri-add-circle-line me-1"></i>
									<span className="btn-text"><JTranslation typeCase="pascal" text={REQUEST_TIME_OFF} /></span>
								</button>
							</div>
							<div className="flex-grow-0 d-flex justify-content-end ">
								<div className="col-md-12 d-flex justify-content-end">
									
									{/* Filter Template */}
									{filterTemplate}

									{/* Search Template */}
									<div className={`ms-2 form-group has-search ${!showTitle ? '' : 'search-small-box'}`}>
										<span className="fa fa-search form-control-feedback">
											<FaSearch />{' '}
										</span>
										<DebounceInput
											onFocusCapture={() => {breakPoint >= width && setShowTitle(false)}}
											onBlurCapture={(event: any) => {                                                
                                                if(breakPoint >= width && event.target.value.length === 0) {
                                                    setShowTitle(true)
                                                }
                                            }}
											className="form-control"
											placeholder={translationText.search}
											minLength={0}
											debounceTimeout={800}
											onChange={(e) => {
												setSearch(e.target.value);
											}}
											value={search}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="card-body flex-grow-1 overflow-auto">
						<div className="survey-section h-100">
							{timeOffRequestList.length ? (
								<UserTimeOffRequestsDataGrid
									tableData={timeOffRequestList}
									setMode={setMode}
									setToastMessage={setToastMessage}
									setShowSideBar={setShowSideBar}
									showSideBar={showSideBar}
									dataTableEvent={dataTableEvent}
									setDataTableEvent={setDataTableEvent}
									paginationData={paginationData}
									loading={timeOffData.isFetching}
								/>
							) : timeOffData.isLoading || timeOffData.isRefetching ? (
								<Loader />
							) : (
								<div className="text-center">
									<img src={TimeOffImage} alt="time-off-request" />
									<h5 className="mt-5 mb-3"><JTranslation typeCase="pascal" text={`No ${capitalize(selectedStatus ?? '')} Time Off Requested`} /></h5>
									<p>
										<JTranslation typeCase="capitalize" text={`Do you need some time off? No worries! Just Send a new request whenever you want
										to schedule some time off, and we'll make sure it's properly handled.`} />
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}
export default withSidebar(TimeOffRequest)
